export type CommandStyleId = "operator" | "architect" | "wave";

export interface CommandStyleColors {
  background: string;      // hex
  surface: string;          // hex (cards)
  surfaceElevated: string;  // hex (modals)
  border: string;           // hex
  primary: string;          // hex (accent)
  primaryHover: string;     // hex
  textPrimary: string;      // hex
  textSecondary: string;    // hex
  success: string;          // hex
  error: string;            // hex
}

export interface CommandStyleCopy {
  // Nav labels
  dashboard: string;
  calendar: string;
  projects: string;
  inbox: string;
  settings: string;
  sessions: string;
  transcripts: string;

  // Generic terms
  tasks: string;
  task: string;
  complete: string;
  completed: string;
  newItem: string;
  connect: string;
  connected: string;
  aiChat: string;

  // Greeting
  greeting: string; // "WHAT'S THE MISSION TODAY, {name}?"

  // Empty states
  emptyDashboard: string;
  emptyProjects: string;
  emptyInbox: string;

  // Loading
  loading: string;

  // Success
  success: string;

  // Error
  error: string;

  // Buttons
  addButton: string; // "+ ADD OPERATION" etc.
  enterButton: string; // "ENTER" / "LAUNCH" / "RUN IT"
  executeButton: string; // "EXECUTE" / "DRAFT" / "LET'S GO"

  // Chat
  chatPlaceholder: string;
  chatIntro: string;

  // Capture bar
  capturePlaceholder: string;

  // Onboarding
  onboardingFinalButton: string;
}

export interface CommandStyle {
  id: CommandStyleId;
  name: string;
  identity: string;
  vibe: string;
  colors: CommandStyleColors;
  copy: CommandStyleCopy;
}

export const COMMAND_STYLES: Record<CommandStyleId, CommandStyle> = {
  operator: {
    id: "operator",
    name: "THE OPERATOR",
    identity: "Quiet. Calculated. Untouchable.",
    vibe: "Special forces. No noise. Every action intentional.",
    colors: {
      background: "#0A0A0A",
      surface: "#111111",
      surfaceElevated: "#1A1A1A",
      border: "#222222",
      primary: "#CE2121",
      primaryHover: "#b01c1c",
      textPrimary: "#FFFFFF",
      textSecondary: "#888888",
      success: "#00FF85",
      error: "#CE2121",
    },
    copy: {
      dashboard: "HQ",
      calendar: "TIMELINE",
      projects: "MISSIONS",
      inbox: "SIGNAL",
      settings: "COMMAND",
      sessions: "SESSIONS",
      transcripts: "DEBRIEFS",
      tasks: "Operations",
      task: "Operation",
      complete: "Executed",
      completed: "Executed",
      newItem: "Deploy",
      connect: "Link Up",
      connected: "LIVE",
      aiChat: "COMMAND AI",
      greeting: "WHAT'S THE MISSION TODAY, {name}?",
      emptyDashboard: "HQ is quiet. Drop your first operation.",
      emptyProjects: "No active operations.",
      emptyInbox: "No signal.",
      loading: "Pulling intel...",
      success: "Executed.",
      error: "Signal lost. Trying again.",
      addButton: "+ DEPLOY",
      enterButton: "ENTER",
      executeButton: "EXECUTE",
      chatPlaceholder: "Give an order...",
      chatIntro: "I'm your Command AI. Give me an order.",
      capturePlaceholder: "Drop a task, idea, or order...",
      onboardingFinalButton: "ENTER COMMAND CENTER",
    },
  },
  architect: {
    id: "architect",
    name: "THE ARCHITECT",
    identity: "Building something the world hasn't seen yet.",
    vibe: "Elon in a server room. Vision-mode. Big picture thinker.",
    colors: {
      background: "#050A14",
      surface: "#0A1628",
      surfaceElevated: "#122040",
      border: "#1A2A4A",
      primary: "#4DAAFF",
      primaryHover: "#6BBBFF",
      textPrimary: "#FFFFFF",
      textSecondary: "#7A8BA8",
      success: "#00FF85",
      error: "#CE2121",
    },
    copy: {
      dashboard: "STUDIO",
      calendar: "ROADMAP",
      projects: "BUILDS",
      inbox: "FEED",
      settings: "SYSTEM",
      sessions: "SESSIONS",
      transcripts: "TRANSCRIPTS",
      tasks: "Builds",
      task: "Build",
      complete: "Shipped",
      completed: "Shipped",
      newItem: "Draft",
      connect: "Integrate",
      connected: "LIVE",
      aiChat: "CO-PILOT",
      greeting: "WHAT ARE YOU BUILDING TODAY, {name}?",
      emptyDashboard: "Clean slate. What are you building?",
      emptyProjects: "No blueprints yet.",
      emptyInbox: "Feed is clear.",
      loading: "Loading your build...",
      success: "Shipped.",
      error: "Something broke in the build. Retrying.",
      addButton: "+ NEW BUILD",
      enterButton: "LAUNCH",
      executeButton: "DRAFT",
      chatPlaceholder: "What are we building?",
      chatIntro: "Co-Pilot online. What are we building?",
      capturePlaceholder: "Drop a build, idea, or blueprint...",
      onboardingFinalButton: "LAUNCH STUDIO",
    },
  },
  wave: {
    id: "wave",
    name: "THE WAVE",
    identity: "In flow. On time. Ahead of everyone.",
    vibe: "Kid Cudi on a good morning. Effortless. Magnetic. Moving different.",
    colors: {
      background: "#080808",
      surface: "#101010",
      surfaceElevated: "#181818",
      border: "#202020",
      primary: "#00FF85",
      primaryHover: "#33FFA3",
      textPrimary: "#FFFFFF",
      textSecondary: "#808080",
      success: "#00FF85",
      error: "#CE2121",
    },
    copy: {
      dashboard: "BASE",
      calendar: "SCHEDULE",
      projects: "RUNS",
      inbox: "DROPS",
      settings: "SETUP",
      sessions: "SESSIONS",
      transcripts: "TAPES",
      tasks: "Moves",
      task: "Move",
      complete: "Done",
      completed: "Done",
      newItem: "Drop",
      connect: "Plug In",
      connected: "LIVE",
      aiChat: "THE WAVE AI",
      greeting: "YOU'RE UP, {name}. LET'S RIDE.",
      emptyDashboard: "Nothing moving yet. Start the wave.",
      emptyProjects: "Nothing in motion yet.",
      emptyInbox: "No drops yet.",
      loading: "Getting things moving...",
      success: "Done. Keep moving.",
      error: "Hit a wall. One sec.",
      addButton: "+ ADD TO THE WAVE",
      enterButton: "RUN IT",
      executeButton: "LET'S GO",
      chatPlaceholder: "What do you need?",
      chatIntro: "I'm live. What do you need?",
      capturePlaceholder: "Drop a move, idea, or vibe...",
      onboardingFinalButton: "LET'S RIDE",
    },
  },
};

// Convert hex to HSL string for CSS variables (Tailwind format: "H S% L%")
export function hexToHSL(hex: string): string {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Compute relative luminance of a hex color (0 = black, 1 = white)
function hexLuminance(hex: string): number {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

// Lighten a hex color by a factor (0-1). factor=0.1 means 10% lighter.
function lightenHex(hex: string, factor: number): string {
  hex = hex.replace("#", "");
  const r = Math.min(255, Math.round(parseInt(hex.substring(0, 2), 16) + (255 - parseInt(hex.substring(0, 2), 16)) * factor));
  const g = Math.min(255, Math.round(parseInt(hex.substring(2, 4), 16) + (255 - parseInt(hex.substring(2, 4), 16)) * factor));
  const b = Math.min(255, Math.round(parseInt(hex.substring(4, 6), 16) + (255 - parseInt(hex.substring(4, 6), 16)) * factor));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

// Apply a command style's colors to CSS variables
export function applyStyleColors(colors: CommandStyleColors): void {
  const root = document.documentElement;

  // Determine if primary color is "light" (needs dark text on it)
  const primaryIsLight = hexLuminance(colors.primary) > 0.4;
  const primaryFg = primaryIsLight ? "0 0% 7%" : "0 0% 100%"; // zinc-950 or white
  const accentFg = primaryFg;

  // Derive muted from surface (slightly lighter)
  const mutedColor = lightenHex(colors.surface, 0.05);
  const secondaryFg = hexToHSL(colors.textPrimary);

  // Core backgrounds
  root.style.setProperty("--background", hexToHSL(colors.background));
  root.style.setProperty("--foreground", hexToHSL(colors.textPrimary));

  // Card
  root.style.setProperty("--card", hexToHSL(colors.surface));
  root.style.setProperty("--card-foreground", hexToHSL(colors.textPrimary));

  // Popover
  root.style.setProperty("--popover", hexToHSL(colors.surfaceElevated));
  root.style.setProperty("--popover-foreground", hexToHSL(colors.textPrimary));

  // Primary
  root.style.setProperty("--primary", hexToHSL(colors.primary));
  root.style.setProperty("--primary-foreground", primaryFg);

  // Secondary
  root.style.setProperty("--secondary", hexToHSL(colors.surface));
  root.style.setProperty("--secondary-foreground", secondaryFg);

  // Accent
  root.style.setProperty("--accent", hexToHSL(colors.primary));
  root.style.setProperty("--accent-foreground", accentFg);

  // Muted
  root.style.setProperty("--muted", hexToHSL(mutedColor));
  root.style.setProperty("--muted-foreground", hexToHSL(colors.textSecondary));

  // Border / Input / Ring
  root.style.setProperty("--border", hexToHSL(colors.border));
  root.style.setProperty("--input", hexToHSL(colors.border));
  root.style.setProperty("--ring", hexToHSL(colors.primary));

  // Destructive (keep standard red)
  root.style.setProperty("--destructive", hexToHSL(colors.error));

  // Chart
  root.style.setProperty("--chart-1", hexToHSL(colors.primary));

  // Sidebar
  root.style.setProperty("--sidebar-background", hexToHSL(colors.background));
  root.style.setProperty("--sidebar-foreground", hexToHSL(colors.textPrimary));
  root.style.setProperty("--sidebar-primary", hexToHSL(colors.primary));
  root.style.setProperty("--sidebar-accent", hexToHSL(colors.surface));
  root.style.setProperty("--sidebar-border", hexToHSL(colors.border));
  root.style.setProperty("--sidebar-ring", hexToHSL(colors.primary));
}
